import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';

/**
 * AgencyCreateClientStep Component
 *
 * Second step of the agency onboarding wizard.
 * Explains creating the first client workspace and provides a CTA
 * that navigates to the Agency Portfolio where the create-workspace
 * flow lives (CreateBrandSpaceModal).
 */

interface AgencyCreateClientStepProps {
  onOpenPortfolio: () => void;
}

export function AgencyCreateClientStep({ onOpenPortfolio }: AgencyCreateClientStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center">
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="material-symbols-outlined text-[44px] text-primary-600" aria-hidden="true">
            add_business
          </span>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {t('onboarding_agency_create_client_title', { defaultValue: 'Create your first brand' })}
      </h2>
      <p className="text-gray-600 mb-8">
        {t('onboarding_agency_create_client_description', { defaultValue: 'Each brand gets a dedicated space. You can add channels, calendars, and publish posts on their behalf.' })}
      </p>

      {/* Steps */}
      <div className="space-y-3 text-left mb-8">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-primary-contrast text-xs font-bold flex items-center justify-center">
            1
          </div>
          <p className="text-sm text-gray-700">
            {t('onboarding_agency_create_client_step1', { defaultValue: 'Give the brand a name' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-primary-contrast text-xs font-bold flex items-center justify-center">
            2
          </div>
          <p className="text-sm text-gray-700">
            {t('onboarding_agency_create_client_step2', { defaultValue: 'Enter the brand and connect its social channels' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-primary-contrast text-xs font-bold flex items-center justify-center">
            3
          </div>
          <p className="text-sm text-gray-700">
            {t('onboarding_agency_create_client_step3', { defaultValue: 'Start creating and scheduling content for them' })}
          </p>
        </div>
      </div>

      {/* CTA */}
      <Button
        variant="primary"
        size="lg"
        onClick={onOpenPortfolio}
        className="w-full"
      >
        <Icon name={ICONS.PLUS_CIRCLE} size="md" color="text-white" className="mr-2" ariaHidden />
        {t('onboarding_agency_create_client_cta', { defaultValue: 'Create first brand' })}
      </Button>

      <p className="mt-3 text-xs text-gray-500">
        {t('onboarding_agency_create_client_hint', { defaultValue: 'You can create more brands anytime from your Brand Portfolio.' })}
      </p>
    </div>
  );
}
